/**
 * Bulk CSV import/export API client (11E.9) — one client for all five
 * entities (students, staff, subjects, packages, sessions). The `entity`
 * path segment selects the backend adapter.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@core/api/client';

export type RowStatus = 'valid' | 'invalid';
export type CommitStatus = 'created' | 'skipped' | 'errored';

export interface RowResult {
  row_number: number;
  status: RowStatus;
  errors: string[];
  natural_key: string | null;
  preview: Record<string, string>;
}

export interface DryRunResult {
  entity: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  header_errors: string[];
  preview: RowResult[];
  all_errors: RowResult[];
}

export interface CommitOutcome {
  row_number: number;
  status: CommitStatus;
  id: string | null;
  error: string | null;
  note: string | null;
}

export interface CommitResult {
  entity: string;
  import_job_id: string;
  created: number;
  skipped: number;
  errored: number;
  outcomes: CommitOutcome[];
  committed_at: string;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function downloadTemplate(entity: string): Promise<void> {
  const blob = await api.get<Blob>(`/v1/imports/${entity}/template.csv`);
  triggerBlobDownload(blob, `${entity}_template.csv`);
}

export interface ExportParams {
  ids?: string[];
  status?: string;
  search?: string;
}

export async function exportEntityCsv(entity: string, params: ExportParams = {}): Promise<void> {
  const blob = await api.get<Blob>(`/v1/imports/${entity}/export.csv`, {
    query: {
      ids: params.ids && params.ids.length > 0 ? params.ids.join(',') : undefined,
      status: params.status,
      search: params.search,
    },
  });
  triggerBlobDownload(blob, `${entity}_export.csv`);
}

export function useDryRunImport(entity: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<DryRunResult>(`/v1/imports/${entity}/dry-run`, form, { multipart: true });
    },
  });
}

/**
 * Each entity's list page owns its own react-query key prefix (see
 * `studentKeys.all` / `staffKeys.all` / `subjectKeys.all` / `packageKeys.all`
 * / `sessionKeys.all` in their respective `api/*.ts`), and several of those
 * prefixes are NOT simply `[entity]` (e.g. subjects/packages are namespaced
 * under `['catalog', ...]`, sessions under `['calendar', ...]`). Map to the
 * real prefixes here rather than guessing `[entity]`, or the page silently
 * keeps showing stale (pre-import) data until the user manually refetches.
 */
const QUERY_KEY_PREFIXES: Record<string, string[][]> = {
  students: [['students'], ['guardians']],
  staff: [['staff']],
  subjects: [['catalog', 'subjects']],
  packages: [['catalog', 'packages']],
  sessions: [['calendar', 'sessions'], ['calendar', 'classes'], ['bookings']],
};

export function useCommitImport(entity: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<CommitResult>(`/v1/imports/${entity}/commit`, form, { multipart: true });
    },
    onSuccess: () => {
      const prefixes = QUERY_KEY_PREFIXES[entity] ?? [[entity]];
      prefixes.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
    },
  });
}
